import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import type {
  PreconditionItem,
  PreconditionStatus,
} from '@sr-test-wizard/shared';
import { JiraKeyLink } from '../../components/JiraKeyLink';
import { usePreconditionsStore } from '../../state/preconditionsStore';
import { PushConfirmModal } from './PushConfirmModal';

const STATUSES: PreconditionStatus[] = ['todo', 'done', 'skipped'];
type StatusFilter = 'all' | PreconditionStatus | 'staged';

function statusLabel(s: PreconditionStatus): string {
  return s === 'todo' ? 'TODO' : s === 'done' ? 'DONE' : 'SKIPPED';
}

function statusBadgeClass(s: PreconditionStatus | undefined): string {
  if (s === 'done') return 'badge good small';
  if (s === 'skipped') return 'badge small';
  return 'badge warn small';
}

interface FilterState {
  search: string;
  status: StatusFilter;
}

/**
 * Phase 0 - Preconditions panel. Compact table layout: each row shows the
 * effective status, the (optional) group, and the truncated label. Clicking
 * a row expands it inline to reveal the PDF-derived guide, the original
 * Smart Checklist body, the status toggle, the note input, and the auto-
 * verify controls. The toolbar exposes a search field, quick status filter
 * pills, pull / push actions, and a single "Verify all" button that runs
 * every applicable verifier with a small concurrency cap.
 *
 * Push staging works the same as before: status changes accumulate into
 * `bundle.staged`, are previewed in the PushConfirmModal, then sent to Jira
 * in one PATCH. WS events from other tabs flip a `dirty` flag that triggers
 * a quiet re-load.
 */
export function PreconditionsPanel() {
  const { id } = useParams<{ id: string }>();
  const runId = id!;
  const bundle = usePreconditionsStore((s) => s.bundle);
  const loading = usePreconditionsStore((s) => s.loading);
  const pulling = usePreconditionsStore((s) => s.pulling);
  const pushing = usePreconditionsStore((s) => s.pushing);
  const error = usePreconditionsStore((s) => s.error);
  const dirty = usePreconditionsStore((s) => s.dirty);
  const verifying = usePreconditionsStore((s) => s.verifying);
  const verifyingAll = usePreconditionsStore((s) => s.verifyingAll);
  const lastVerifyAllSummary = usePreconditionsStore((s) => s.lastVerifyAllSummary);
  const load = usePreconditionsStore((s) => s.load);
  const pull = usePreconditionsStore((s) => s.pull);
  const setStatus = usePreconditionsStore((s) => s.setStatus);
  const setNote = usePreconditionsStore((s) => s.setNote);
  const verify = usePreconditionsStore((s) => s.verify);
  const verifyAll = usePreconditionsStore((s) => s.verifyAll);
  const push = usePreconditionsStore((s) => s.push);

  const [showConfirm, setShowConfirm] = useState(false);
  const [pushFeedback, setPushFeedback] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [filters, setFilters] = useState<FilterState>({ search: '', status: 'all' });

  useEffect(() => {
    if (runId) void load(runId);
  }, [runId, load]);

  useEffect(() => {
    if (runId && dirty) void load(runId, { force: true });
  }, [runId, dirty, load]);

  const stagedCount = useMemo(
    () => Object.values(bundle?.staged ?? {}).filter((s) => s.status !== undefined).length,
    [bundle],
  );
  const stats = useMemo(() => {
    if (!bundle) return null;
    const total = bundle.items.length;
    const done = bundle.items.filter((i) => {
      const effective = bundle.staged[i.id]?.status ?? i.status;
      return effective === 'done' || effective === 'skipped';
    }).length;
    return { total, done };
  }, [bundle]);

  const rows = useMemo(() => {
    if (!bundle) return [] as Array<{ item: PreconditionItem; effective: PreconditionStatus; staged: boolean; note: string | null }>;
    const term = filters.search.trim().toLowerCase();
    return bundle.items
      .map((item) => {
        const stage = bundle.staged[item.id];
        const effective = stage?.status ?? item.status;
        const staged = stage?.status !== undefined && stage.status !== item.status;
        const note = stage?.note ?? null;
        return { item, effective, staged, note };
      })
      .filter(({ item, effective, staged }) => {
        if (filters.status === 'staged' && !staged) return false;
        if (filters.status !== 'all' && filters.status !== 'staged' && filters.status !== effective)
          return false;
        if (!term) return true;
        return (
          item.label.toLowerCase().includes(term) ||
          item.group.toLowerCase().includes(term) ||
          (item.body ?? '').toLowerCase().includes(term)
        );
      });
  }, [bundle, filters]);

  const handlePush = async () => {
    try {
      const result = await push(runId);
      setPushFeedback(`Pushed ${result.pushed} change(s) to ${result.tpKey}.`);
      setShowConfirm(false);
    } catch (err) {
      setPushFeedback(`Push failed: ${(err as Error).message}`);
    }
  };

  return (
    <div className="flex-col">
      <div className="card phase-action-card">
        <div className="row">
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={{ margin: 0 }}>Phase 0 - Preconditions</h2>
            <p className="muted small" style={{ margin: '4px 0 0' }}>
              {bundle?.tpKey ? (
                <>
                  Mirror of{' '}
                  <JiraKeyLink keyId={bundle.tpKey} className="badge mono small" />
                  {bundle.tpSummary && <> {bundle.tpSummary}</>}
                  {stats && (
                    <>
                      {' '}· {stats.done}/{stats.total} done
                    </>
                  )}
                  {stagedCount > 0 && (
                    <> · <span className="badge warn small">{stagedCount} staged</span></>
                  )}
                </>
              ) : bundle ? (
                <span className="muted">No Testing Preconditions task found under {bundle.srKey}.</span>
              ) : (
                <span className="muted">Pull preconditions to begin.</span>
              )}
            </p>
          </div>
          <span className="spacer" />
          <button
            className="btn small"
            disabled={pulling || pushing}
            onClick={() => void pull(runId)}
          >
            {pulling ? 'Pulling...' : bundle?.tpKey ? 'Refresh from Jira' : 'Pull from Jira'}
          </button>
          <button
            className="btn small"
            disabled={
              verifyingAll ||
              pulling ||
              pushing ||
              !bundle ||
              bundle.items.length === 0
            }
            onClick={() => void verifyAll(runId)}
            title="Run every applicable verifier (deterministic checks first, AI as fallback)."
          >
            {verifyingAll ? 'Verifying...' : 'Verify all'}
          </button>
          <button
            className="btn btn-primary small"
            disabled={stagedCount === 0 || pushing}
            onClick={() => setShowConfirm(true)}
            title={stagedCount === 0 ? 'Stage at least one change to push' : 'Push staged changes to Jira'}
          >
            {pushing ? 'Pushing...' : `Push ${stagedCount} change${stagedCount === 1 ? '' : 's'} to Jira`}
          </button>
        </div>
        {error && <p style={{ color: 'var(--bad)', marginTop: 8 }}>{error}</p>}
        {lastVerifyAllSummary && (
          <p className="muted small" style={{ marginTop: 8 }}>
            Verify all: {lastVerifyAllSummary.done} done, {lastVerifyAllSummary.todo} todo,
            {' '}{lastVerifyAllSummary.skipped} skipped, {lastVerifyAllSummary.manual} manual
            {lastVerifyAllSummary.failed > 0 && (
              <> &middot; {lastVerifyAllSummary.failed} failed</>
            )}{' '}
            &middot; {(lastVerifyAllSummary.elapsedMs / 1000).toFixed(1)}s
          </p>
        )}
        {pushFeedback && (
          <p
            className="small"
            style={{
              marginTop: 8,
              color: pushFeedback.startsWith('Push failed') ? 'var(--bad)' : 'var(--good)',
            }}
          >
            {pushFeedback}
          </p>
        )}
        {bundle?.meta?.staleReason && (
          <p className="small muted" style={{ marginTop: 8 }}>
            {bundle.meta.staleReason}
          </p>
        )}
      </div>

      {!bundle && loading && (
        <div className="card">
          <p className="muted">Loading preconditions...</p>
        </div>
      )}

      {bundle && bundle.items.length === 0 && (
        <div className="card">
          <p className="muted">
            {bundle.tpKey
              ? 'The TP task has no Smart Checklist items, or the checklist field could not be detected.'
              : 'No Testing Preconditions task linked to this SR. Pulling will retry.'}
          </p>
        </div>
      )}

      {bundle && bundle.items.length > 0 && (
        <div className="card">
          <FilterBar
            filters={filters}
            onChange={setFilters}
            total={bundle.items.length}
            shown={rows.length}
            stagedCount={stagedCount}
          />
          <div className="data-table-wrap precondition-table-wrap">
            <table className="data-table precondition-table">
              <colgroup>
                <col style={{ width: 70 }} />
                <col style={{ width: 160 }} />
                <col />
                <col style={{ width: 36 }} />
              </colgroup>
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Group</th>
                  <th>Item</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="muted small" style={{ textAlign: 'center' }}>
                      No items match the current filter.
                    </td>
                  </tr>
                ) : (
                  rows.map(({ item, effective, staged, note }) => {
                    const isOpen = expandedId === item.id;
                    return (
                      <PreconditionRow
                        key={item.id}
                        item={item}
                        effective={effective}
                        staged={staged}
                        note={note}
                        suggestionApplies={!!item.suggestedStatus && item.suggestedStatus !== 'manual'}
                        expanded={isOpen}
                        verifying={verifying.has(item.id)}
                        onToggle={() => setExpandedId(isOpen ? null : item.id)}
                        onStatus={(s) => void setStatus(runId, item.id, s)}
                        onNote={(n) => void setNote(runId, item.id, n)}
                        onVerify={() => void verify(runId, item.id)}
                        onApplySuggestion={() => {
                          const suggested = item.suggestedStatus;
                          if (suggested && suggested !== 'manual') {
                            void setStatus(runId, item.id, suggested);
                          }
                        }}
                        onClearStage={() => {
                          if (staged) void setStatus(runId, item.id, item.status);
                          if (note) void setNote(runId, item.id, null);
                        }}
                      />
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showConfirm && bundle && (
        <PushConfirmModal
          bundle={bundle}
          pushing={pushing}
          onCancel={() => setShowConfirm(false)}
          onConfirm={() => void handlePush()}
        />
      )}
    </div>
  );
}

interface FilterBarProps {
  filters: FilterState;
  onChange: (next: FilterState) => void;
  total: number;
  shown: number;
  stagedCount: number;
}

function FilterBar({ filters, onChange, total, shown, stagedCount }: FilterBarProps) {
  const pills: Array<{ key: StatusFilter; label: string }> = [
    { key: 'all', label: 'All' },
    { key: 'todo', label: 'TODO' },
    { key: 'done', label: 'DONE' },
    { key: 'skipped', label: 'SKIPPED' },
    { key: 'staged', label: `Staged${stagedCount > 0 ? ` (${stagedCount})` : ''}` },
  ];
  return (
    <div className="row precondition-filter-bar" style={{ gap: 8, flexWrap: 'wrap' }}>
      <input
        value={filters.search}
        onChange={(e) => onChange({ ...filters, search: e.target.value })}
        placeholder="Filter by label, group, body..."
        style={{ minWidth: 240, flex: 1 }}
      />
      <div className="row" style={{ gap: 0 }}>
        {pills.map((p, idx) => (
          <button
            key={p.key}
            type="button"
            className={`btn small ${filters.status === p.key ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => onChange({ ...filters, status: p.key })}
            style={{
              borderTopRightRadius: idx === pills.length - 1 ? undefined : 0,
              borderBottomRightRadius: idx === pills.length - 1 ? undefined : 0,
              borderTopLeftRadius: idx === 0 ? undefined : 0,
              borderBottomLeftRadius: idx === 0 ? undefined : 0,
            }}
          >
            {p.label}
          </button>
        ))}
      </div>
      <span className="muted small" style={{ marginLeft: 'auto' }}>
        {shown === total ? `${total} items` : `${shown} of ${total}`}
      </span>
    </div>
  );
}

interface PreconditionRowProps {
  item: PreconditionItem;
  effective: PreconditionStatus;
  staged: boolean;
  note: string | null;
  suggestionApplies: boolean;
  expanded: boolean;
  verifying: boolean;
  onToggle: () => void;
  onStatus: (s: PreconditionStatus) => void;
  onNote: (n: string | null) => void;
  onVerify: () => void;
  onApplySuggestion: () => void;
  onClearStage: () => void;
}

function PreconditionRow({
  item,
  effective,
  staged,
  note,
  suggestionApplies,
  expanded,
  verifying,
  onToggle,
  onStatus,
  onNote,
  onVerify,
  onApplySuggestion,
  onClearStage,
}: PreconditionRowProps) {
  return (
    <>
      <tr className="row-clickable" onClick={onToggle}>
        <td>
          <span className={statusBadgeClass(effective)}>{statusLabel(effective)}</span>
        </td>
        <td>
          {item.group ? (
            <span className="muted small" title={item.group}>
              {item.group}
            </span>
          ) : (
            <span className="muted small">-</span>
          )}
        </td>
        <td className="precondition-label-cell">
          <span className="precondition-label-text" title={item.label}>
            {item.label}
          </span>
          {staged && <span className="badge warn small">staged</span>}
          {suggestionApplies && !staged && (
            <span className="badge info small">suggest</span>
          )}
          {note && <span className="badge small">note</span>}
        </td>
        <td className="precondition-chevron">
          <span aria-hidden="true">{expanded ? '\u25BC' : '\u25B6'}</span>
        </td>
      </tr>
      {expanded && (
        <tr className="env-link-detail-row">
          <td colSpan={4}>
            <PreconditionDetail
              item={item}
              effective={effective}
              staged={staged}
              note={note}
              verifying={verifying}
              onStatus={onStatus}
              onNote={onNote}
              onVerify={onVerify}
              onApplySuggestion={onApplySuggestion}
              onClearStage={onClearStage}
            />
          </td>
        </tr>
      )}
    </>
  );
}

interface DetailProps {
  item: PreconditionItem;
  effective: PreconditionStatus;
  staged: boolean;
  note: string | null;
  verifying: boolean;
  onStatus: (s: PreconditionStatus) => void;
  onNote: (n: string | null) => void;
  onVerify: () => void;
  onApplySuggestion: () => void;
  onClearStage: () => void;
}

function PreconditionDetail({
  item,
  effective,
  staged,
  note,
  verifying,
  onStatus,
  onNote,
  onVerify,
  onApplySuggestion,
  onClearStage,
}: DetailProps) {
  return (
    <div className="precondition-detail" onClick={(e) => e.stopPropagation()}>
      <div className="row" style={{ flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        <span className={statusBadgeClass(item.status)}>Jira: {statusLabel(item.status)}</span>
        {staged && (
          <span className="badge warn small">staged -&gt; {statusLabel(effective)}</span>
        )}
        {item.group && <span className="muted small">in &ldquo;{item.group}&rdquo;</span>}
      </div>
      <div className="precondition-detail-label">{item.label}</div>
      {item.guide && (
        <div className="precondition-guide ai-card small">
          <div className="muted small precondition-detail-section-label">Guide</div>
          <ReactMarkdown>{item.guide}</ReactMarkdown>
        </div>
      )}
      {item.body && (
        <div className="ai-card precondition-body">
          <ReactMarkdown>{item.body}</ReactMarkdown>
        </div>
      )}

      <div className="precondition-detail-section">
        <div className="muted small precondition-detail-section-label">
          Status (local stage)
        </div>
        <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
          {STATUSES.map((s) => (
            <button
              key={s}
              className={`btn small ${effective === s ? 'btn-primary' : ''}`}
              onClick={() => onStatus(s)}
              type="button"
            >
              {statusLabel(s)}
            </button>
          ))}
          {(staged || note) && (
            <button className="btn btn-ghost small" onClick={onClearStage} type="button">
              Clear stage
            </button>
          )}
        </div>
      </div>

      <div className="precondition-detail-section">
        <div className="muted small precondition-detail-section-label">
          Note (local only, not pushed)
        </div>
        <textarea
          value={note ?? ''}
          rows={2}
          placeholder="Add a local note for your records..."
          onChange={(e) => onNote(e.target.value || null)}
          style={{ width: '100%' }}
        />
      </div>

      <div className="precondition-detail-section">
        <div className="row" style={{ gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          <button className="btn small" disabled={verifying} onClick={onVerify} type="button">
            {verifying ? 'Verifying...' : 'Run auto-verify'}
          </button>
          {item.suggestedStatus && item.suggestedStatus !== 'manual' && (
            <button className="btn btn-ghost small" onClick={onApplySuggestion} type="button">
              Apply suggestion ({statusLabel(item.suggestedStatus as PreconditionStatus)})
            </button>
          )}
        </div>
        {item.suggestedStatus && (
          <div className="precondition-suggestion small">
            <div className="muted" style={{ marginBottom: 2 }}>
              Suggestion: <strong>{item.suggestedStatus.toUpperCase()}</strong>
            </div>
            {item.suggestedEvidence && <div>{item.suggestedEvidence}</div>}
          </div>
        )}
      </div>
    </div>
  );
}
